import { useEffect, useState, useMemo, useCallback } from 'react';
import useFiltersState from 'state/useFiltersState';
import { ISharedModules, ORDER_PROPERTIES_TO_SEARCH } from 'types/orders';
import { getNudosData } from 'utils/getNudosData';
import { bordColorTheme } from 'styles/styles.constants';
import ChargerModule from 'components/chargerModule';
import CustomSelect from 'components/customSelect';
import OrdersListingFinances from './components/OrdersListingFinances/OrdersListingFinances';
import OrdersListingOperations from './components/OrdersListingOperations/OrdersListingOperations';
import './TrackingOrder.scss';

enum AREA_USER {
  FINANCES = 'Finanzas',
  OPERATIONS = 'Operaciones'
}

enum PERMISSION_GROUP_NAMES {
  FINANCES = 'finance',
  OPERATIONS = 'operations'
}

/**
 * Componente principal para el tracking de órdenes.
 * Muestra las órdenes en formato Trello con dos módulos: Finanzas y Operaciones.
 * Cada módulo hijo maneja sus propias llamadas API de forma independiente.
 */
const TrackingOrder = () => {
  const {
    numberOfOrders,
    sharedModule,
    setSharedModule,
    ordersSearchbarText,
    setOrdersSearchbarText,
    propertyToFilterSearch,
    setPropertyToFilterSearch
  } = useFiltersState();

  const loggedUserData = getNudosData();
  const permissions = loggedUserData?.permissions;
  const permissionGroup = permissions?.permissionGroups;
  const permissionFinance = permissionGroup?.find(group => group?.name === PERMISSION_GROUP_NAMES.FINANCES);
  const permissionOperations = permissionGroup?.find(group => group?.name === PERMISSION_GROUP_NAMES.OPERATIONS);
  const hasPermissionFinance = !!permissionFinance;
  const hasPermissionOperations = !!permissionOperations;

  const initialSharedModules = useMemo<ISharedModules[]>(() => {
    const modules: ISharedModules[] = [];

    if (hasPermissionFinance) {
      modules.push({ title: AREA_USER.FINANCES, key: 1, active: true });
    }

    if (hasPermissionOperations) {
      modules.push({
        title: AREA_USER.OPERATIONS,
        key: 2,
        active: !hasPermissionFinance
      });
    }

    if (modules?.length === 0) {
      modules.push({ title: AREA_USER.OPERATIONS, key: 2, active: true });
    }

    return modules;
  }, [hasPermissionFinance, hasPermissionOperations]);

  const [sharedModules, setSharedModules] = useState<ISharedModules[]>(initialSharedModules);

  const headerSubtitle = `Compras ${typeof numberOfOrders === 'number' ? numberOfOrders : ''}`;
  const headerInfo = { title: 'Órdenes', titleRoute: '/order/main', subtitle: headerSubtitle };
  const isModuleFirstKey = sharedModule?.key === 1;
  const orderPropertiesToSearchValues = Object.values(ORDER_PROPERTIES_TO_SEARCH);
  const orderPropertiesToSearchKeys = Object.keys(ORDER_PROPERTIES_TO_SEARCH);

  /**
   * Maneja el cambio de propiedad de búsqueda en el filtro.
   * @param value - Valor de la propiedad seleccionada
   */
  const handleSetPropertyToFilterSearch = (value: ORDER_PROPERTIES_TO_SEARCH) =>
    setPropertyToFilterSearch({
      name: value,
      propertyToFilter: orderPropertiesToSearchKeys?.find(
        key => ORDER_PROPERTIES_TO_SEARCH[key as keyof typeof ORDER_PROPERTIES_TO_SEARCH] === value
      )
    });

  /**
   * Obtiene el placeholder del searchbar según el tipo de búsqueda seleccionado.
   * @returns Texto placeholder para el searchbar
   */
  const getSearchbarPlaceholder = () => {
    switch (propertyToFilterSearch?.name) {
      case ORDER_PROPERTIES_TO_SEARCH.ORGANIZATION_NAME:
        return 'Escribe el nombre de empresa';
      case ORDER_PROPERTIES_TO_SEARCH.SERIAL_NUMBER:
        return 'Escribe el número del serial';
      case ORDER_PROPERTIES_TO_SEARCH.EMPLOYEE_NAME:
        return 'Escribe el nombre del empleado';
      case ORDER_PROPERTIES_TO_SEARCH.PURCHASE_ORDER:
        return 'Escribe el número de la orden de compra (PO)';
      default:
        return 'Escribe el número de orden';
    }
  };

  /**
   * Cambia el módulo activo al que corresponde a la key proporcionada.
   * @param key - Key del módulo a activar
   */
  const sharedModulesAction = (key: number) => {
    const newList: ISharedModules[] = [];
    for (const module of sharedModules) {
      const newModule = {
        key: module.key,
        title: module.title,
        active: module.key === key
      };
      if (newModule.active) setSharedModule(newModule);
      newList.push(newModule);
    }
    setSharedModules([...newList]);
  };

  /**
   * Sincroniza los módulos compartidos con los permisos del usuario.
   */
  const syncSharedModules = useCallback(() => {
    setSharedModules(initialSharedModules);
    const activeModule = initialSharedModules.find(module => module.active);
    const currentModuleExists = initialSharedModules.some(module => module.key === sharedModule?.key);

    if (activeModule && (!sharedModule || !currentModuleExists)) {
      setSharedModule(activeModule);
    }
  }, [initialSharedModules, sharedModule, setSharedModule]);

  useEffect(() => {
    syncSharedModules();
  }, [syncSharedModules]);

  return (
    <div id="trackingOrderModule">
      <ChargerModule
        infoSideBar={{ position: 'trackingOrder' }}
        headerInfo={headerInfo}
        viewSearchBar
        searchBarDropdownList={
          isModuleFirstKey ? (
            <CustomSelect
              customClassName="customSelectTrackingOrder"
              containerOptionsClassName="containerOptionsTrackingOrder"
              customBackground={bordColorTheme.white.default}
              customBackgroundEmpty={bordColorTheme.white.default}
              dropdownLabel="Tipo de búsqueda"
              items={orderPropertiesToSearchValues}
              value={propertyToFilterSearch?.name}
              onChangeSelect={value => handleSetPropertyToFilterSearch(value as ORDER_PROPERTIES_TO_SEARCH)}
            />
          ) : undefined
        }
        searchText={ordersSearchbarText}
        setSearchText={setOrdersSearchbarText}
        searchbarPlaceholder={getSearchbarPlaceholder()}
        sharedModules={sharedModules}
        sharedModule={sharedModule}
        sharedModulesAction={sharedModulesAction}
      >
        {isModuleFirstKey ? <OrdersListingFinances /> : <OrdersListingOperations />}
      </ChargerModule>
    </div>
  );
};

export default TrackingOrder;
